// strategy/match.ts - ruleMatches(rule, ctx): does an ExploitRule's `when` fire on this context?
// ENGINE_DESIGN.md §6.2: "ALL present conditions must match"; every omitted field is a wildcard, and
// array fields are OR-matches. This is a pure field comparison - the whole point of the rule format
// is that firing is data, not code.
import { handClassRank } from '@postflop/schema';
/** True iff every present condition in `rule.when` is satisfied by `ctx`. */
export const ruleMatches = (rule, ctx) => {
    const w = rule.when;
    if (w.street && !w.street.includes(ctx.street))
        return false;
    if (w.villainArchetype && !w.villainArchetype.includes(ctx.villain.archetype.id))
        return false;
    if (w.villainImbalance) {
        const profile = ctx.villain.archetype.imbalances;
        for (const [axis, val] of Object.entries(w.villainImbalance)) {
            if (profile[axis] !== val)
                return false;
        }
    }
    if (w.heroHandClass) {
        const hc = w.heroHandClass;
        const rank = handClassRank(ctx.hero.handClass);
        if (hc.in && !hc.in.includes(ctx.hero.handClass))
            return false;
        if (hc.min !== undefined && rank < handClassRank(hc.min))
            return false;
        if (hc.max !== undefined && rank > handClassRank(hc.max))
            return false;
    }
    if (w.heroDrawFlags) {
        const flags = ctx.hero.drawFlags;
        if (w.heroDrawFlags.any && !w.heroDrawFlags.any.some((f) => flags.includes(f)))
            return false;
        if (w.heroDrawFlags.none && w.heroDrawFlags.none.some((f) => flags.includes(f)))
            return false;
    }
    if (w.textureClass && !w.textureClass.includes(ctx.textureClass))
        return false;
    if (w.sprBucket && !w.sprBucket.includes(ctx.sprBucket))
        return false;
    if (w.positionClass && !w.positionClass.includes(ctx.positionClass))
        return false;
    if (w.facing && !w.facing.includes(ctx.facing))
        return false;
    if (w.potType && !w.potType.includes(ctx.potType))
        return false;
    if (w.playersInHand) {
        if (w.playersInHand.min !== undefined && ctx.playersInHand < w.playersInHand.min)
            return false;
        if (w.playersInHand.max !== undefined && ctx.playersInHand > w.playersInHand.max)
            return false;
    }
    if (w.icmPressure) {
        // absent ICM context ⇒ 'none'; a rule that requires moderate/severe simply won't match a cash spot.
        const pressure = ctx.icm?.pressure ?? 'none';
        if (!w.icmPressure.includes(pressure))
            return false;
    }
    if (w.heroCovers !== undefined) {
        const covers = ctx.icm?.heroCovers ?? false;
        if (covers !== w.heroCovers)
            return false;
    }
    if (w.tournamentStage) {
        // absent tournament stage ⇒ a cash spot; a rule that gates on stage simply won't match it (so a
        // bubble/final-table rule cannot leak into cash). In a tournament context the stage must be one
        // the rule names. Mirrors the icmPressure branch above.
        if (ctx.tournamentStage === undefined || !w.tournamentStage.includes(ctx.tournamentStage)) {
            return false;
        }
    }
    return true;
};
//# sourceMappingURL=match.js.map